import { landmineNames } from '@mm/constants/landmine-names';
import { DragIcon } from '@mm/icons';
import { PageBillingType } from '@mm/state/interfaces/connected-page';
import ButtonModel, { ButtonType } from '@mm/state/model/ButtonModel';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { ReactComponent as FancyTrashIcon } from 'assets/garbage.svg';
import { ReactComponent as PathIcon } from 'assets/paths-icon.svg';
import { ReactComponent as TrashIcon } from 'assets/trash-icon.svg';
import classnames from 'classnames';
import pluralize from 'pluralize';
import React, { useState } from 'react';
import { UncontrolledTooltip } from 'reactstrap';
import './ButtonComponent.scss';

type Props = {
  entity: ButtonModel;
  index: number;
  onClick: (index: number) => () => void;
  onDelete: () => void;
  mainMenuSelected: boolean;
  withBorders?: boolean;
  className?: string;
  renderDragIcon?: boolean;
  useFancyTrashIcon?: boolean;
  renderPathIcon?: boolean;
};

const REMOVE_MOBILE_MONKEY_MENU_ITEM_TEXT =
  '"Powered by MobileMonkey" is removed in paid version';

export const ButtonComponent = (props: Props) => {
  const {
    entity,
    mainMenuSelected,
    index,
    onClick,
    onDelete,
    withBorders = true,
    renderDragIcon = false,
    useFancyTrashIcon = false,
    className,
    renderPathIcon,
  } = props;

  const modalActions = useModalActions();

  const [hover, setHover] = useState(false);

  const onMouseEnter = () => setHover(true);
  const onMouseLeave = () => setHover(false);
  const onClickLocal = () => onClick && onClick(index);
  const onDeleteClick = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    onDelete();
  };
  const onPowerByMobileMonkeyClick = () =>
    modalActions.openUniversalUpgradeLandmineModal({
      landmineName: landmineNames['WIDGETS__BUTTONS__POWERED-BY-MM'],
      planFallback: PageBillingType.Pro,
    });
  // Removing ability to delete Powered By MobileMonkey Main Menu Item
  const disallowModification = mainMenuSelected && entity.sortOrder === 999;
  const { clickThrough } = entity;
  const clicks = entity.clicksCount || 0;

  let clickThroughString;

  if (entity.type === ButtonType.URL) {
    clickThroughString = `${clicks} ${pluralize('click', clicks)}`;
  } else {
    clickThroughString =
      clickThrough || clickThrough === 0
        ? clickThrough === 'N/A'
          ? clickThrough
          : `${clickThrough}%`
        : null;
  }

  const { viewsCount, type } = entity;
  const TrashIconComponent = useFancyTrashIcon ? FancyTrashIcon : TrashIcon;
  const viewCountString =
    type === ButtonType.CALL ? '' : `/${viewsCount ?? 'N/A'}`;

  return (
    <div
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      className={className}
    >
      <div
        className="button-component__click-through--indicator"
        style={{
          width:
            !!clickThrough && clickThrough !== 'N/A'
              ? `${clickThrough}%`
              : `0%`,
        }}
      />
      <div
        className={classnames(
          hover
            ? 'button-component__outer-container--hover'
            : 'button-component__outer-container',
          {
            'button-component__outer-container--border': withBorders,
          },
        )}
      >
        <div
          className="button-component__inner-container"
          onClick={
            disallowModification ? onPowerByMobileMonkeyClick : onClickLocal
          }
        >
          <div
            id={disallowModification ? 'tooltip-target' : undefined}
            className="button-component__title"
          >
            {renderPathIcon && (
              <PathIcon className="button-component__path-icon" />
            )}
            {entity.name}
          </div>
          <div
            id={disallowModification ? 'tooltip-target' : undefined}
            className="button-component__stats"
          >
            <div className="button-component__click-through">
              {clickThroughString}
              {viewCountString}
            </div>
          </div>
          {disallowModification ? (
            <UncontrolledTooltip
              className="button-component__icon-tooltip"
              target="tooltip-target"
              placement="top"
            >
              {REMOVE_MOBILE_MONKEY_MENU_ITEM_TEXT}
            </UncontrolledTooltip>
          ) : null}
          {renderDragIcon && (
            <DragIcon className="button-component__drag-icon" />
          )}
          {disallowModification ? null : (
            <TrashIconComponent
              className="button-component__trash-icon"
              onClick={onDeleteClick}
            />
          )}
        </div>
      </div>
    </div>
  );
};
